import { usePrevious } from "@chakra-ui/react";
import { getSounds } from "@imaldev/imal-factory/abc";
import { ELocale } from "@imaldev/imal-factory/i18n";
import { useLocale } from "@imaldev/imal-react-ui/i18n";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useWDocumentData, wSetDoc, wUpdateDoc } from "../../../../../api/shared";
import { useUser } from "../../../../../api/user/context";
import { IS_FAST_DEV } from "../../../../../constants";
import {
  getBookSequence,
  SoundSequence
} from "../../../../../utils/utilsiMAL/soundSequences/sequences";
import { WithChildren } from "../../../../../utils/utilsReact/types";
import { PLACEHOLDER } from "../../../../../utils/utilsTS/misc";
import { UUID } from "../../../../../utils/utilsTS/uuid/uuid";

/* This gets new prop defaultTypeface. */
/* Save typeface -> then get typeface from this... */
/* How will this typeface be used throughout the app? */
/* SelectFont gets the defaultTypeface. with a hook? */

type TSheetsAbcUserData = {
  access: {
    hasRequestedTrial: boolean;
    hasPaidAccess: boolean;
  };
  idDefaultSequence: string;
  idDefaultTypeface: string;
  metadata: {
    dateLastUsage: Date;
  };
};

const placeholderData: TSheetsAbcUserData = {
  idDefaultSequence: IS_FAST_DEV ? "FAST_DEV" : PLACEHOLDER,
  idDefaultTypeface: IS_FAST_DEV ? "FAST_DEV" : PLACEHOLDER,
  access: {
    hasRequestedTrial: false,
    hasPaidAccess: false
  },
  metadata: {
    dateLastUsage: new Date(0)
  }
};

export const ContextSheetsAbcUserData = createContext<{
  data: TSheetsAbcUserData;
  haveData: boolean;
}>(null!);

/* Should default typeface be... in a context? or just normal hook? */

/* TODO: Should always have retrieved user when rendering this provider? */
/* If that's true, won't need checks on `haveUser`. */
export const ProviderAppSheetsAbcUserData = ({ children }: WithChildren) => {
  const { locale } = useLocale();
  const { haveUser, user } = useUser();
  const docPath = useMemo(() => `/app_sheets_abc/${locale}/users/${user.id}/`, [locale, user.id]);
  /* What happens when data doesn't match the type/missing fields?? */
  /* Can write code so don't matter? */
  const [appData, isLoadingData] = useWDocumentData<TSheetsAbcUserData>(
    user.id === "" ? "/foo/bar" : docPath
  );

  useEffect(() => {
    /* Create doc on initial login. */
    if (haveUser && !isLoadingData && !appData) {
      wSetDoc(docPath, placeholderData);
    }
  }, [appData, isLoadingData, haveUser, user, locale]);

  return (
    <ContextSheetsAbcUserData.Provider
      value={{
        data: appData ?? placeholderData,
        haveData: haveUser && !isLoadingData
      }}
    >
      {children}
    </ContextSheetsAbcUserData.Provider>
  );
};

/* Put some things in config? Ie. two nested objects: */
/* config and access. and then trial obj inside access? */
export const useAppSheetsAbcUserData = () => {
  return useContext(ContextSheetsAbcUserData);
};

const phSeq: SoundSequence = {
  bookPicture: "lmao",
  displayName: "Placeholder",
  id: "placeholder",
  name: "foobar",
  sounds: getSounds(ELocale.de_DE)
};

type TContextDefaultSequence = {
  defaultSequence: SoundSequence;
  hasDefaultSequence: boolean;
  isLoadingSequence: boolean;
  setDefaultSequence: (sequenceId: string) => void;
};

export const ContextDefaultSequence = createContext<TContextDefaultSequence>(null!);

export const ProviderDefaultSequence = ({ children = undefined as JSX.Element | undefined }) => {
  const { data: userData, haveData } = useAppSheetsAbcUserData();
  const { locale } = useLocale();
  const { user } = useUser();
  const [dbSeq] = IS_FAST_DEV
    ? [phSeq]
    : useWDocumentData<SoundSequence>(
        `app_sheets_abc/${locale}/users/${user.id === "" ? "foobar" : user.id}/sequences/${
          userData.idDefaultSequence ?? "foobarId"
        }`
      );

  const defaultSeq = useMemo(
    () => (IS_FAST_DEV ? phSeq : getBookSequence(locale, userData.idDefaultSequence) ?? dbSeq),
    [dbSeq?.id, userData.idDefaultSequence, locale]
  );

  const prevDefault: SoundSequence | undefined = usePrevious(defaultSeq);

  const setDefaultSequence = useCallback(
    (seqId: UUID) => {
      wUpdateDoc(`/app_sheets_abc/${locale}/users/${user.id}`, {
        idDefaultSequence: seqId
      });
    },
    [locale, user.id]
  );

  useEffect(() => console.log(defaultSeq), [defaultSeq]);

  return (
    <ContextDefaultSequence.Provider
      value={
        IS_FAST_DEV
          ? {
              defaultSequence: phSeq,
              hasDefaultSequence: true,
              isLoadingSequence: false,
              setDefaultSequence: () => {}
            }
          : {
              defaultSequence: defaultSeq ?? prevDefault ?? phSeq,
              hasDefaultSequence: userData.idDefaultSequence !== "placeholder",
              isLoadingSequence: !haveData,
              setDefaultSequence
            }
      }
    >
      {children}
    </ContextDefaultSequence.Provider>
  );
};

export const useDefaultSequence = () => {
  return useContext(ContextDefaultSequence);
};
